/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type CSSProperties } from 'react';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { type Image } from '@/types';
import { cn } from '@/lib/utils';
import DynamicImage from '@/components/dynamic-image';

/* ============================================================================
   CONSTANTS & VALIDATION
   ============================================================================ */

/** Regex for valid anchor IDs: letters, numbers, hyphens, underscores only */
const ANCHOR_ID_REGEX = /^[a-zA-Z0-9_-]+$/;

/** Regex for validating hex colors */
const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

/**
 * Validates anchor ID format
 * - Must contain only: a-z, A-Z, 0-9, hyphens, underscores
 * - No spaces or special characters
 */
function isValidAnchorId(id: string | undefined): boolean {
    if (!id || typeof id !== 'string') return false;
    const trimmed = id.trim();
    return trimmed.length > 0 && ANCHOR_ID_REGEX.test(trimmed);
}

/**
 * Validates hex color format
 */
function isValidHexColor(color: string | undefined): boolean {
    if (!color || typeof color !== 'string') return false;
    return HEX_COLOR_REGEX.test(color.trim());
}

/**
 * Parses optional hex color and returns valid color or undefined
 */
function parseOptionalHex(value: string | undefined): string | undefined {
    const trimmed = value?.trim();
    if (!trimmed || !HEX_COLOR_REGEX.test(trimmed)) return undefined;
    return trimmed;
}

/**
 * Resolves text color style based on white flag or hex color
 */
function resolveTextColor(isWhite: boolean | undefined, hexColor: string | undefined): CSSProperties | undefined {
    if (isWhite) return { color: '#ffffff' };
    const hex = parseOptionalHex(hexColor);
    return hex ? { color: hex } : undefined;
}

/**
 * Resolves CTA href from URL and/or anchor
 */
function resolveCtaHref(url: string | undefined, anchor: string | undefined): string {
    const trimmedAnchor = anchor?.trim();
    if (trimmedAnchor) return `#${trimmedAnchor}`;
    return url?.trim() ?? '';
}

/**
 * Returns a background-mode embed URL when the value is a Vimeo player page
 * (e.g. https://player.vimeo.com/video/123?badge=0). These are HTML pages, not
 * video files, so they must be rendered in an iframe rather than a <video> tag.
 */
function getVimeoEmbedUrl(url: string | undefined): string | undefined {
    if (!url) return undefined;
    try {
        const parsed = new URL(url.trim());
        if (parsed.hostname !== 'player.vimeo.com') return undefined;
        parsed.searchParams.set('background', '1');
        parsed.searchParams.set('autoplay', '1');
        parsed.searchParams.set('muted', '1');
        parsed.searchParams.set('loop', '1');
        parsed.searchParams.set('autopause', '0');
        return parsed.toString();
    } catch {
        return undefined;
    }
}

/** CTA actions that must be applied as className; all other data-action values use the data-action attribute. */
const CLASS_BASED_ACTIONS = new Set(['signup-sms']);

/* ============================================================================
   PAGE DESIGNER METADATA & DECORATORS
   ============================================================================ */

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately */
@Component('inGridBanners', {
    name: 'SFN In-Grid Banners',
    description:
        'Promotional banner component that displays inline within a product grid on PLP. Supports 1x, 2x, or full-width layouts on desktop with responsive backgrounds (image/video) for desktop and mobile. Features optional eyebrow image, headline, subheadline, CTA, and customizable text colors with anchor linking.',
    group: 'Layout',
})
export class InGridBannersMetadata {
    // GENERAL
    @AttributeDefinition({
        id: 'uniqueId',
        name: 'Unique ID',
        description: 'Optional unique identifier applied as the HTML id on this component. Used to target this component with custom CSS.',
    })
    uniqueId?: string;

    @AttributeDefinition({
        id: 'componentWidth',
        name: 'Width',
        description: 'Display width of this component on the page.',
        type: 'enum',
        values: ['Full Width', '90% Width'],
        defaultValue: 'Full Width',
    })
    componentWidth?: string;

    @AttributeDefinition({
        id: 'anchorId',
        name: 'Anchor ID',
        description:
            'Unique identifier for anchor linking. Allowed: letters, numbers, hyphens, underscores. No spaces.',
        type: 'string',
        required: false,
    })
    anchorId?: string;

    @AttributeDefinition({
        id: 'bannerWidth',
        name: 'Banner Width',
        description: 'Width of banner on desktop: 1 product tile, 2 product tiles, or full column width.',
        type: 'enum',
        values: ['1 Tile', '2 Tiles', 'Full Width'],
        defaultValue: '1 Tile',
    })
    bannerWidth?: string;

    // BACKGROUND MEDIA - DESKTOP
    @AttributeDefinition({
        id: 'backgroundImageDesktop',
        name: 'Background Image - Desktop',
        description: 'Image to display as background on desktop viewport.',
        type: 'image',
        required: false,
    })
    backgroundImageDesktop?: Image;

    @AttributeDefinition({
        id: 'backgroundVideoDesktop',
        name: 'Background Video URL - Desktop',
        description: 'URL for video to display as background on desktop viewport (MP4 or WebM).',
        type: 'string',
        required: false,
    })
    backgroundVideoDesktop?: string;

    @AttributeDefinition({
        id: 'backgroundAltTextDesktop',
        name: 'Background Alt Text - Desktop',
        description: 'Alternative text for desktop background image or video.',
        type: 'string',
        required: false,
    })
    backgroundAltTextDesktop?: string;

    // BACKGROUND MEDIA - MOBILE
    @AttributeDefinition({
        id: 'backgroundImageMobile',
        name: 'Background Image - Mobile',
        description: 'Image to display as background on mobile viewport.',
        type: 'image',
        required: false,
    })
    backgroundImageMobile?: Image;

    @AttributeDefinition({
        id: 'backgroundVideoMobile',
        name: 'Background Video URL - Mobile',
        description: 'URL for video to display as background on mobile viewport (MP4 or WebM).',
        type: 'string',
        required: false,
    })
    backgroundVideoMobile?: string;

    @AttributeDefinition({
        id: 'backgroundAltTextMobile',
        name: 'Background Alt Text - Mobile',
        description: 'Alternative text for mobile background image or video.',
        type: 'string',
        required: false,
    })
    backgroundAltTextMobile?: string;

    // EYEBROW
    @AttributeDefinition({
        id: 'eyebrowImage',
        name: 'Eyebrow Image',
        description: 'Optional image to display above headline.',
        type: 'image',
        required: false,
    })
    eyebrowImage?: Image;

    @AttributeDefinition({
        id: 'eyebrowAltText',
        name: 'Eyebrow Image Alt Text',
        description: 'Alternative text for eyebrow image.',
        type: 'string',
        required: false,
    })
    eyebrowAltText?: string;

    // CONTENT
    @AttributeDefinition({
        id: 'headline',
        name: 'Headline',
        description: 'Main headline text.',
        type: 'string',
        required: false,
    })
    headline?: string;

    @AttributeDefinition({
        id: 'subheadline',
        name: 'Subheadline',
        description: 'Secondary headline text.',
        type: 'string',
        required: false,
    })
    subheadline?: string;

    // TEXT COLORS
    @AttributeDefinition({
        id: 'textColorWhite',
        name: 'Use White Text Color',
        description: 'When selected, text (headline and subheadline) will be white.',
        type: 'boolean',
        defaultValue: false,
    })
    textColorWhite?: boolean;

    @AttributeDefinition({
        id: 'textColorHex',
        name: 'Text Color (Hex)',
        description: 'Hex color for text (headline and subheadline). e.g., #FFFFFF',
        type: 'string',
        required: false,
    })
    textColorHex?: string;

    // CTA
    @AttributeDefinition({
        id: 'ctaText',
        name: 'CTA Text',
        description: 'Text for the call-to-action button.',
        type: 'string',
        required: false,
    })
    ctaText?: string;

    @AttributeDefinition({
        id: 'ctaUrl',
        name: 'CTA URL',
        description: 'URL destination for CTA. Supports product, category, page, content, search, or external URLs.',
        type: 'url',
        required: false,
    })
    ctaUrl?: string;

    @AttributeDefinition({
        id: 'ctaAnchor',
        name: 'CTA Anchor',
        description: 'Anchor ID of another component on the page to scroll to.',
        type: 'string',
        required: false,
    })
    ctaAnchor?: string;

    // CTA TEXT COLOR
    @AttributeDefinition({
        id: 'ctaColorWhite',
        name: 'CTA Text Color White',
        description: 'When selected, CTA text will be white.',
        type: 'boolean',
        defaultValue: false,
    })
    ctaColorWhite?: boolean;

    @AttributeDefinition({
        id: 'ctaColorHex',
        name: 'CTA Text Color (Hex)',
        description: 'Hex color for CTA text. e.g., #000000',
        type: 'string',
        required: false,
    })
    ctaColorHex?: string;

    @AttributeDefinition({
        id: 'ctaNewTab',
        name: 'Open CTA in New Tab',
        description: 'When selected, CTA opens in a new tab.',
        type: 'boolean',
        defaultValue: false,
    })
    ctaNewTab?: boolean;

    // DESTINATION LINK
    @AttributeDefinition({
        id: 'destinationLink',
        name: 'Destination Landing Page',
        description: 'Optional link for the destination landing page. Supports product, category, page, content, search, or external URLs.',
        type: 'url',
        required: false,
    })
    destinationLink?: string;
}
/* v8 ignore stop */

/* ============================================================================
   COMPONENT TYPES & INTERFACES
   ============================================================================ */

export interface InGridBannersProps {
    // General
    uniqueId?: string;
    componentWidth?: string;
    anchorId?: string;
    bannerWidth?: string;

    // Background media - desktop
    backgroundImageDesktop?: Image;
    backgroundVideoDesktop?: string;
    backgroundAltTextDesktop?: string;

    // Background media - mobile
    backgroundImageMobile?: Image;
    backgroundVideoMobile?: string;
    backgroundAltTextMobile?: string;

    // Eyebrow
    eyebrowImage?: Image;
    eyebrowAltText?: string;

    // Content
    headline?: string;
    subheadline?: string;

    // Text colors
    textColorWhite?: boolean;
    textColorHex?: string;

    // CTA
    ctaText?: string;
    ctaUrl?: string;
    ctaAnchor?: string;
    ctaColorWhite?: boolean;
    ctaColorHex?: string;
    ctaNewTab?: boolean;

    // Destination link
    destinationLink?: string;

    // Layout hooks (set by the PLP grid, not Page Designer)
    className?: string;
    style?: CSSProperties;
    /** Desktop column count of the parent PLP grid (3 or 4). Used to compute accurate DIS image widths. */
    desktopColumns?: 3 | 4;
    /** Whether the PLP refinements panel is open. Reduces available grid width by ~20vw. */
    hasRefinementsPanel?: boolean;
}

/* ============================================================================
   COMPONENT IMPLEMENTATION
   ============================================================================ */

export default function InGridBanners({
    uniqueId,
    componentWidth: _componentWidth,
    anchorId,
    bannerWidth = '1 Tile',
    backgroundImageDesktop,
    backgroundVideoDesktop,
    backgroundAltTextDesktop,
    backgroundImageMobile,
    backgroundVideoMobile,
    backgroundAltTextMobile,
    eyebrowImage,
    eyebrowAltText,
    headline,
    subheadline,
    textColorWhite,
    textColorHex,
    ctaText,
    ctaUrl,
    ctaAnchor,
    ctaColorWhite,
    ctaColorHex,
    ctaNewTab = false,
    destinationLink,
    className,
    style,
    desktopColumns = 3,
    hasRefinementsPanel = true,
}: InGridBannersProps): ReactElement {
    // Validate and normalize anchor ID
    const validAnchorId = uniqueId?.trim() || (isValidAnchorId(anchorId) ? anchorId.trim() : undefined);
    const widthClass = _componentWidth === '90% Width' ? 'w-[90%] max-w-[1980px] mx-auto' : undefined;

    // Determine width + height classes per tile type
   const getWidthClasses = (width: string): string => {
    switch (width) {
        case '2 Tiles':
            // md:row-span-8 matches the product tile's row-span-8 subgrid so the banner
            // occupies the same 8 tracks as adjacent tiles, preventing a permanent
            // 1-track offset in subsequent product rows.
            // md:min-h-[321px] provides a non-zero intrinsic height in Page Designer preview
            // (no CSS grid parent). In the PLP grid, align-self:stretch overrides h-auto and
            // fills the 8 row tracks regardless of min-height.
            return 'col-span-full w-full h-[321px] md:col-span-2 md:h-auto md:row-span-8 md:min-h-[321px]';

        case 'Full Width':
            // Full Width takes the entire row — no adjacent product tiles to stretch against.
            // Desktop height is one 3-column tile wide (width / 3), the same way a PLP tile's height
            // follows its column width, so it scales with the tiles. Same for 3- and 4-column grids.
            return 'col-span-full w-full h-[321px] md:h-auto md:aspect-[3/1]';

        case '1 Tile':
        default:
            return 'col-span-full w-full h-[321px] md:col-span-1 md:h-auto md:row-span-8 md:min-h-[321px]';
    }
};

    // Get background media for desktop and mobile
    // Each viewport uses its own media; if only one is configured it is used for both so the banner is never blank.
    const configuredDesktopUrl = backgroundImageDesktop?.url || backgroundVideoDesktop;
    const configuredMobileUrl = backgroundImageMobile?.url || backgroundVideoMobile;
    const desktopBackgroundUrl = configuredDesktopUrl || configuredMobileUrl;
    const mobileBackgroundUrl = configuredMobileUrl || configuredDesktopUrl;
    const desktopAltText = backgroundAltTextDesktop || backgroundAltTextMobile;
    const mobileAltText = backgroundAltTextMobile || backgroundAltTextDesktop;

    // True once the deferred inGridSlotsContent Promise has resolved and merged its data in.
    // Layout-only slots carry only `bannerWidth`; all media/text arrive via the deferred promise.
    const hasContent = !!(
        backgroundImageDesktop?.url ||
        backgroundImageMobile?.url ||
        backgroundVideoDesktop?.trim() ||
        backgroundVideoMobile?.trim() ||
        headline?.trim() ||
        subheadline?.trim() ||
        eyebrowImage?.url
    );

    // Resolve text colors
    const textStyle = resolveTextColor(textColorWhite, textColorHex);
    const ctaTextStyle = resolveTextColor(ctaColorWhite, ctaColorHex);

    // Determine if media is a video
    const isVideoUrl = (url: string | undefined): boolean => {
        if (!url) return false;
        return /\.(mp4|webm|mov|avi|mkv)$/i.test(url) || url.includes('video') || url.includes('vimeo');
    };

    // Check if we have a CTA
    const hasCta = !!(ctaText?.trim() || ctaUrl?.trim() || ctaAnchor?.trim() || destinationLink?.trim());
    const ctaHref = resolveCtaHref(ctaUrl || destinationLink, ctaAnchor);
    const isWideLayout = bannerWidth === 'Full Width' || bannerWidth === '2 Tiles';

const _getImageClass = () => {
    switch (bannerWidth) {
        case 'Full Width':
            // Fixed 418px desktop height: fill the full width and keep proportions (crops overflow).
            return 'absolute inset-0 w-full h-full object-cover object-center';
        case '2 Tiles':
        case '1 Tile':
        default:
            // 1 Tile / 2 Tiles share a row with product tiles. Keeping the image absolutely
            // positioned means the section's height comes from the grid (row-span-8 tracks),
            // matching the adjacent product tile height. A static image here would add its
            // natural dimensions to the row height and distort the grid layout.
            return 'absolute inset-0 w-full h-full object-cover object-center';
    }
};

    // Pixel widths for DIS-responsive background images per breakpoint.
    // Formula: tilePercent × maxViewportPx × panelFactor
    //   tilePercent: 67% (2T/3col), 50% (2T/4col), 33% (1T/3col), 25% (1T/4col)
    //   maxViewportPx: lg = 1280px; xl = 2560px (covers retina at 1280 CSS px)
    //   panelFactor: 0.78 when refinements panel is open, 1.0 otherwise
    // Mobile (base/sm/md): banners are col-span-full → ~90vw.
    const panelFactor = hasRefinementsPanel ? 0.78 : 1.0;
    let backgroundImageWidths: (number | string)[];
    if (bannerWidth === 'Full Width') {
        const gridVw = hasRefinementsPanel ? 70 : 90;
        backgroundImageWidths = ['90vw', '90vw', '90vw', `${gridVw}vw`, `${gridVw}vw`, `${gridVw}vw`];
    } else {
        const tilePercent =
            bannerWidth === '2 Tiles'
                ? desktopColumns === 3 ? 0.67 : 0.50
                : desktopColumns === 3 ? 0.335 : 0.25; // 1 Tile
        const lgPx = Math.round(tilePercent * 1280 * panelFactor);
        const xlPx = Math.round(tilePercent * 2560 * panelFactor);
        backgroundImageWidths = ['90vw', '90vw', '90vw', lgPx, xlPx, xlPx];
    }

    // Render background media
    const renderMedia = (
        backgroundUrl: string,
        backgroundAltText: string | undefined,
        visibilityClass?: string
    ): ReactElement => {
        const vimeoEmbedUrl = getVimeoEmbedUrl(backgroundUrl);
        if (vimeoEmbedUrl) {
            // Oversized 16:9 iframe centered in the banner so it covers the area (like object-cover).
            return (
                <iframe
                    src={vimeoEmbedUrl}
                    title={backgroundAltText || 'Background video'}
                    allow="autoplay; fullscreen; picture-in-picture"
                    loading="lazy"
                    tabIndex={-1}
                    className={cn(
                        'absolute left-1/2 top-1/2 aspect-video min-h-full min-w-full -translate-x-1/2 -translate-y-1/2 pointer-events-none border-0',
                        visibilityClass
                    )}
                />
            );
        }

        if (isVideoUrl(backgroundUrl)) {
            return (
                <video
                    src={backgroundUrl}
                    autoPlay
                    muted
                    playsInline
                    loop
                    aria-label={backgroundAltText || undefined}
                    className={cn('absolute inset-0 w-full h-full object-cover', visibilityClass)}
                />
            );
        }

        // Bake q=80 into the URL so DIS uses higher quality for in-grid banner images.
        // An existing q= param in the source URL is preserved as-is.
        const srcWithQuality = /[?&]q=/.test(backgroundUrl)
            ? backgroundUrl
            : `${backgroundUrl}${backgroundUrl.includes('?') ? '&' : '?'}q=80`;

        return (
            <DynamicImage
                src={srcWithQuality}
                alt={backgroundAltText ?? ''}
                className={cn('absolute inset-0 w-full h-full', visibilityClass)}
                imageProps={{ className: 'w-full h-full' }}
                widths={backgroundImageWidths}
                loading="lazy"
            />
        );
    };

    // Render background media: one layer per viewport, swapped at the md breakpoint.
    const renderBackgroundMedia = (): ReactElement | null => {
        if (!desktopBackgroundUrl && !mobileBackgroundUrl) {
            return <div className="absolute inset-0 bg-muted" />;
        }

        // Same media for both viewports: render once, no breakpoint toggling.
        if (desktopBackgroundUrl === mobileBackgroundUrl) {
            return renderMedia(desktopBackgroundUrl!, desktopAltText);
        }

        return (
            <>
                {renderMedia(mobileBackgroundUrl!, mobileAltText, 'md:hidden')}
                {renderMedia(desktopBackgroundUrl!, desktopAltText, 'hidden md:block')}
            </>
        );
    };

    return (
        <section
            {...(validAnchorId && { id: validAnchorId })}
            style={style}
            className={cn('relative w-full overflow-hidden mb-[5px]', getWidthClasses(bannerWidth), widthClass, className)}>
            {/* Single child div so React updates the same DOM node when hasContent flips:
                skeleton classes → plp-tile-fade-in triggers the CSS animation. */}
            <div className={!hasContent
                ? 'absolute inset-0 animate-pulse !bg-[#F0F0F0]'
                : 'absolute inset-0 plp-tile-fade-in'}>
                {hasContent && (
                    <>
                        {/* Background Media */}
                        {renderBackgroundMedia()}

                        {/* Content Overlay */}
                        <div
                            className={cn(
                                'absolute inset-0 z-10 flex flex-col',
                                isWideLayout
                                    ? 'justify-end items-start p-8 md:p-12'
                                    : 'items-start justify-end p-8 md:p-12'
                            )}>
                            <div className={cn(
                                'w-full flex flex-col',
                                isWideLayout ? 'gap-6 items-start' : 'gap-6 items-start',
                            )}>
                                {/* Eyebrow Image */}
                                {eyebrowImage?.url && (
                                    <DynamicImage
                                        src={eyebrowImage.url}
                                        alt={eyebrowAltText ?? ''}
                                        className={cn('max-w-[60px] md:max-w-[80px] h-auto', bannerWidth !== '2 Tiles' && 'mb-3')}
                                        imageProps={{ className: 'w-full h-auto' }}
                                        widths={[80]}
                                        heights={[80]}
                                        loading="lazy"
                                    />
                                )}

                                {/* Headline */}
                                {headline && (
                                    <h2
                                        className={cn(
                                            isWideLayout
                                                ? 'text-2xl md:text-4xl font-bold leading-[22px] md:leading-[40px] tracking-[0.01em] md:tracking-[-0.9px] text-white self-stretch'
                                                : 'text-2xl md:text-[32px] font-bold leading-[22px] tracking-[0.01em] text-white self-stretch'
                                        )}
                                        style={
                                            isWideLayout
                                                ? ({
                                                      color: '#FFF',
                                                      textShadow: '4px 4px 10px rgba(0, 0, 0, 0.50)',
                                                      fontFamily: 'var(--font-family-font-sans, "Neuzeit Grotesk")',
                                                      leadingTrim: 'both',
                                                      textEdge: 'cap',
                                                  } as CSSProperties)
                                                : ({
                                                      color: '#FFF',
                                                      textShadow: '4px 4px 10px rgba(0, 0, 0, 0.50)',
                                                      fontFamily: 'var(--font-family-font-sans, "Neuzeit Grotesk")',
                                                      leadingTrim: 'both',
                                                      textEdge: 'cap',
                                                  } as CSSProperties)
                                        }>
                                        {headline}
                                    </h2>
                                )}

                                {/* Subheadline */}
                                {subheadline && (
                                    <p
                                        className={cn('text-sm md:text-base font-semibold capitalize text-center', bannerWidth !== '2 Tiles' && 'mb-4')}
                                        style={textStyle}>
                                        {subheadline}
                                    </p>
                                )}

                                {/* CTA */}
                                {hasCta && (
                                    <div className="flex">
                                        <a
                                            href={ctaHref || '#'}
                                            target={ctaNewTab ? '_blank' : undefined}
                                            rel={ctaNewTab ? 'noopener noreferrer' : undefined}
                                            className={cn(
                                                isWideLayout
                                                    ? 'text-[14px] md:text-sm font-normal leading-[18px] md:leading-none underline decoration-solid text-white'
                                                    : 'text-[14px] md:text-[15px] font-normal leading-[18px] underline decoration-solid text-white'
                                            )}
                                            style={
                                                ({
                                                    color: '#FFF',
                                                    fontFamily: 'var(--font-family-font-sans, "Neuzeit Grotesk")',
                                                    textDecorationSkipInk: 'none',
                                                    textDecorationThickness: 'auto',
                                                    textUnderlineOffset: 'auto',
                                                    leadingTrim: 'both',
                                                    textEdge: 'cap',
                                                } as CSSProperties)
                                            }>
                                            {ctaText || ctaHref}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
